import { Component, ElementRef, ViewChild, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Observable, throwError, of, catchError, filter, finalize, switchMap } from 'rxjs';

import { Store } from '@ngxs/store';
import { TsSnackbarService } from 'tsui';
import { UserState } from '@state/user/state';
import { Account, User } from '@core/models';
import { RelatedToType } from '@shared/enums';
import { AccountState } from '@state/account/state';
import { FormFieldComponent } from '../form-field.types';
import { LookupService } from '@services/lookup.service';
import { Contact } from '@modules/contacts/contacts.types';
import { Company } from '@modules/companies/companies.types';
import { FormFieldUtils } from '@shared/Utils/form-field.utils';
import { PermissionUtils } from '@core/utils/permission.utils';
import { Folders } from '@shared/modules/folders/state/actions';
import { ProjectsService } from '@modules/projects/projects.service';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { Project, Property } from '@modules/projects/projects.types';
import { AccountFoldersService } from '@modules/folders/folders.service';
import { ConfirmService } from '../../widgets/dialog/confirm/confirm.service';
import { PropertiesService } from '@modules/properties/properties.service';
import { FoldersTabService } from '@shared/modules/folders/folders.service';
import { Task, TaskStatus } from '@tasks/tasks.types';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { FileResource, FolderResource } from '@shared/modules/folders/folders.types';
import { AggregatedTasksService } from '@modules/aggregated-tasks/aggregated-tasks.service';
import { FoldersTabViewFileComponent } from '@shared/modules/folders/view-file/view-file.component';
import { CustomObject, CustomObjectValue } from '@shared/modules/custom-objects/custom-objects.types';
import { AddProjectModalComponent } from '@shared/modals/add-project-modal/add-project-modal.component';
import { AddPropertyModalComponent } from '@shared/modals/add-property-modal/add-property-modal.component';
import { ObjectsManagerService } from '@modules/settings/objects-manager/services/objects-manager.service';
import { ProjectAddCompanyComponent } from '@modules/projects/modals/project-add-company/project-add-company.component';
import { ProjectAddContactComponent } from '@modules/projects/modals/project-add-contact/project-add-contact.component';
import { AddLinkToCustomObjectValueDialog } from '@shared/components/add-link-to-custom-object-value/add-link-to-custom-object-value.component';

const STANDARD_RELATED_TO_TYPES = [
  RelatedToType.Project,
  RelatedToType.Property,
  RelatedToType.Company,
  RelatedToType.Contact,
  RelatedToType.User,
  RelatedToType.FileResource,
  RelatedToType.Task,
  RelatedToType.Engagement,
];

@Component({
  templateUrl: './contact-form-field.component.html',
  styleUrls: ['./contact-form-field.component.scss'],
})
export class ContactFormFieldComponent extends FormFieldComponent {
  @ViewChild('inputControl') inputControl: ElementRef;

  private account: Account;
  private _project: Project;
  private _originalValue: any;
  private editing = false;
  private isNewModel = false;
  private fileResourceMap = {};
  private customObjectId: number;
  private customObjects = this.store.selectSnapshot(ObjectsManagerState.get);
  private customObject: CustomObject;
  private isNewCrmOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_CRM));

  public value: any;
  public customObjType: string;
  public workflowStates: TaskStatus[];
  public activeUsers: User[];
  public fileResource: FileResource;
  public uploadingFile = false;
  public isCustomObjectLookup = false;
  public multipleUserIds: number[];
  public searchOptions: any[] = [];
  public isLoadingOptions = false;
  public itemKey = 'value';
  public itemLabel = 'label';
  public tsSelectValue: any = null;
  public searchTerm: string = '';

  readonly newPermissionsEnabled = this.store.selectSnapshot(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );

  private get isRelatedToUser(): boolean {
    return this.fieldDef?.meta?.record_type === RelatedToType.User;
  }

  private get isRelatedToTask(): boolean {
    return this.fieldDef?.related_to_type === RelatedToType.Task;
  }

  private get isRelatedToCO(): boolean {
    return !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.related_to_type);
  }

  get displayValue(): string {
    return 'REDACTED';
  }

  get isMultipleField(): boolean {
    return this.fieldDef?.meta?.multi_select;
  }

  get hasImageRecordFormat(): boolean {
    return this.fieldDef?.meta?.allowed_record_formats?.includes('image');
  }

  get imageRecordFormats(): string[] {
    return FormFieldUtils.imageRecordFormats;
  }

  get acceptAttribute(): string {
    return FormFieldUtils.getInputAcceptFileAttribute(
      (this.fieldDef?.meta?.allowed_record_formats as FormFieldUtils.AllowedRecordFormats[]) || ['any'],
    );
  }

  get canAddNewRecord(): boolean {
    return (
      this.isCustomObjectLookup && !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.meta?.record_type as RelatedToType)
    );
  }

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _matDialog: MatDialog,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _aggregatedTasksService: AggregatedTasksService,
    private readonly _accountFoldersService: AccountFoldersService,
    private readonly foldersTabService: FoldersTabService,
    private readonly projectsService: ProjectsService,
    private readonly snackBar: TsSnackbarService,
    private readonly objectsManagerService: ObjectsManagerService,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
    private readonly lookupService: LookupService,
    private readonly propertiesService: PropertiesService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
    this._aggregatedTasksService.accountId = this.account.id;
    this.foldersTabService.accountId = this.account.id;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.value = this.model[this.fieldDef.name];

    if (this.isMultipleField && this.isRelatedToUser) {
      this.multipleUserIds = this.value?.map((val: string) => +val.split('|')[0]);
    }

    this.parseContactType();

    this._originalValue = this.value;

    this.isNewModel = this.model?.id == null;

    this.customObjType = this.fieldDef?.meta?.record_type;

    this.customObject = this.data?.customObject;

    this.isCustomObjectLookup =
      (this.fieldDef?.name?.startsWith('cf_linked_') || this.isFieldMetaRelatedToCO()) ?? false;

    this.handleCustomObjType();

    this.setTsSelectValue();

    this.objectsManagerService
      .get(Number(this.account.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customObjects) => {
        this.handleCustomObjectId(customObjects);
      });
  }

  private isFieldMetaRelatedToCO(): boolean {
    return !STANDARD_RELATED_TO_TYPES.includes(
      this.fieldDef?.meta?.record_type || this.fieldDef?.meta?.related_to_type,
    );
  }

  canShowAssociationButton(): boolean {
    return ((!this.value || this.value?.length === 0) && this.isMultipleField) || !this.isDisabled;
  }

  private handleCustomObjType(): void {
    if (this.fieldDef?.meta?.related_to_type === RelatedToType.Task) {
      this.customObjType = RelatedToType.Task;
    }

    switch (this.customObjType) {
      case RelatedToType.FileResource:
        if (this.fieldDef.name in this.model && !this.isMultipleField) {
          this.setFolderSettings();

          const fieldValue = this.model[this.fieldDef.name]?.includes('|')
            ? this.model[this.fieldDef.name].split('|')[0]
            : this.model[this.fieldDef.name];

          if (fieldValue) {
            this.store
              .dispatch(new Folders.GetFileById(+fieldValue))
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);
                this.fileResource = fileResource;
              });
          }
        }

        break;

      case RelatedToType.Task:
        if (this.value) this.value = JSON.parse(String(this.value));
        this._aggregatedTasksService
          .getTaskStatuses()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((taskStatuses) => {
            this.workflowStates = taskStatuses;
          });
        break;

      case RelatedToType.Project:
        this.getProjectData();
        break;
    }
  }

  goToRecord(item?: any): void {
    if (this.customObjType === RelatedToType.FileResource) {
      this.viewFileFromFileResourceCustomObject(item);
      return;
    }

    if (this.isDisabled) {
      return;
    }

    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    let vals = this.getValues();

    if (item) {
      vals = item.split('|');
    }

    const id = vals[0];

    const redirectConfig = this.store.selectSnapshot(ObjectsManagerState.getRedirectConfig)[this.customObjType];

    if ([RelatedToType.Contact, RelatedToType.Company].includes(this.customObjType as RelatedToType)) {
      if (this.isNewCrmOn) {
        this._router.navigate(['/space/void'], { queryParams: { ...redirectConfig.getQueryParams(id) } });
      } else {
        const obj = this.store.selectSnapshot(ObjectsManagerState.get).find((x) => x.name === this.customObjType);
        this._router.navigate(['/', obj.apiResponseConfig.api_path, id]);
      }
    } else if (this.customObjType == RelatedToType.Task) {
      this._router.navigate([
        '/projects',
        this.model.pipeline_id,
        this.value.project_id,
        'tasks',
        this.value.task_list_id,
        this.value.id,
      ]);
    } else if (this.customObjType == RelatedToType.Property) {
      this._router.navigateByUrl(`/space/void?return=/space/properties/0/${id}`);
    } else if (this.isCustomObjectLookup) {
      if (this.customObjType == RelatedToType.Property) {
        this._router.navigateByUrl(`/space/void?return=/space/properties/0/${id}`);
      } else if (this.customObjType == RelatedToType.Project) {
        this._router.navigate(['/projects', this._project?.pipeline_id, id]);
      } else {
        this._router.navigate(['/space/void'], { queryParams: { ...redirectConfig.getQueryParams(id) } });
      }
    } else {
      this.foldersTabService
        .getFile(this.model.root_folder_resource_id, id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((fileResource) => {
          this.viewFile(fileResource);
        });
    }
  }

  updateTask(task: Task): void {
    this._aggregatedTasksService.simpleUpdateTask(task).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private viewFileFromFileResourceCustomObject(item?: any): void {
    if (this.fileResourceMap[item]) {
      this.viewFile(this.fileResourceMap[item]);
      return;
    } else {
      const fieldValue = item?.includes('|') ? item.split('|')[0] : item;

      if (fieldValue) {
        this.store
          .dispatch(new Folders.GetFileById(+fieldValue))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);
            this.fileResourceMap[item] = fileResource;
            this.viewFile(fileResource);
          });
      }
    }
  }

  public openTypeRelatedModal(): void {
    const data = {
      panelClass: 'mailbox-compose-dialog',
      width: '40vw',
      height: 'auto',
      data: {
        byPass: true,
        model: this.model,
        multi_select: this.fieldDef.meta.multi_select,
        related_to_type: this.fieldDef.related_to_type,
        searchTerm: this.searchTerm,
        creationMode: true,
      },
    };
    const customObjectData = {
      width: '40vw',
      height: 'auto',
      data: {
        customObjectType: this.customObjType,
        customObjectId: this.customObjectId,
        related_to_type: this.fieldDef.related_to_type,
        model: this.model,
        canAddNewRecord:
          this.isCustomObjectLookup &&
          !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.meta?.record_type as RelatedToType),
        creationMode: true,
        searchTerm: this.searchTerm,
      },
    };

    let dialogRef: MatDialogRef<any>;

    if (this.customObjType == RelatedToType.Contact) {
      dialogRef = this._matDialog.open(ProjectAddContactComponent, data);
    } else if (this.customObjType == RelatedToType.Company) {
      dialogRef = this._matDialog.open(ProjectAddCompanyComponent, data);
    } else if (this.customObjType == RelatedToType.Project) {
      dialogRef = this._matDialog.open(AddProjectModalComponent, data);
    } else if (this.customObjType == RelatedToType.Property) {
      dialogRef = this._matDialog.open(AddPropertyModalComponent, data);
    } else if (this.isCustomObjectLookup) {
      dialogRef = this._matDialog.open(AddLinkToCustomObjectValueDialog, customObjectData);
    }

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result: { model: any; addedExistedRecord: boolean }) => {
        const fieldValue = this.setFieldValueAfterDialogClose(result);
        this.value = fieldValue;
        this.onSave(this.value);
        this.setTsSelectValue();

        // If this form is being used before a model has been saved i.e. the create form.
        if (this.isNewModel) {
          // JERRY FOR OPENAREA
          if (
            (this.account?.subdomain == 'openarea' || this.account?.subdomain == 'area') &&
            this.customObjType == RelatedToType.Company
          ) {
            this.update.emit({ ['title']: `${result?.model?.name} Deal` });
          }
        }
      });
  }

  removeModelField(event: Event): void {
    event.stopPropagation();

    if (this.customObjType === RelatedToType.FileResource && !this.fileResource) {
      this.errorSnackBar();
      return;
    }

    if (this.isNewModel) {
      this.updateModel(null);
      this.update.emit({ [this.fieldDef.name]: null });
      this._changeDetectorRef.markForCheck();
      this.value = null;
    } else {
      this.confirmService
        .openConfirmationModal()
        .pipe(filter((result: boolean) => result))
        .subscribe(() => {
          this.updateModel(null);
          this.update.emit({ [this.fieldDef.name]: null });
          this._changeDetectorRef.markForCheck();
          this.value = null;
          this._originalValue = null;
        });
    }
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.value = this.model[this.fieldDef.name];
    if (this.isMultipleField && this.isRelatedToUser) {
      this.multipleUserIds = this.value?.map((val: string) => +val.split('|')[0]);
    }
    this.parseContactType();
    this._originalValue = this.model[this.fieldDef.name];
    this.setTsSelectValue();
    this._changeDetectorRef.markForCheck();
  }

  private setFieldValueAfterDialogClose(result: { model: any; addedExistedRecord: boolean }): unknown {
    const { model, addedExistedRecord } = result;
    let fieldValue: unknown;

    // If this is a custom object lookup, we need to get the field value from the custom object's search field
    if (
      this.isCustomObjectLookup &&
      !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.meta?.record_type as RelatedToType)
    ) {
      // Find the custom object context using the customObjectId
      const contextCustomObject = this.customObjects.find((x) => x.id === Number(this.customObjectId));
      // Get the name of the search field that was used to find this custom object value
      const searchFieldName = contextCustomObject.fields.find(
        (x) => x.id === contextCustomObject.search_custom_field_id,
      )?.name;

      // Get the value of the search field from the result's model data if it's a new record, otherwise use the name field
      // if the search field is not found, send an empty string since we don't know what field to display.
      fieldValue = (addedExistedRecord ? model.name : model[searchFieldName]) ?? 'Search field missing';
    } else {
      // For standard lookups (non-custom objects), use the model's name as the field value
      fieldValue = model.name;
    }

    const formattedValue = fieldValue === '' ? model.id : `${model.id}|${fieldValue}`;

    if (this.isMultipleField) {
      const value = this.value ? [...this.value, formattedValue] : [formattedValue];
      return [...new Set(value)];
    } else {
      return formattedValue;
    }
  }

  private getValues() {
    if (this.isNewModel) {
      return [this.model[this.fieldDef.name], this.value];
    } else {
      if (this.model[this.fieldDef.name] && typeof this.model[this.fieldDef.name] === 'string')
        return this.model[this.fieldDef.name].split('|');
      else return null;
    }
  }

  private parseContactType(): void {
    const customObjType = this.fieldDef?.meta?.record_type;
    const values = this.getValues();

    if (this.value && values && values.length == 2) {
      if (customObjType == RelatedToType.User) {
        this.value = values[0];
        this.value = parseInt(this.value);
      } else {
        this.value = values[1];
      }
    }
  }

  removeItem(item: string): void {
    this.value = this.value.filter((val) => val !== item);
    this.update.emit({ [this.fieldDef.name]: this.value });
    this.updateModel(this.value);
    this._changeDetectorRef.markForCheck();
  }

  private onSave(value: any, pushNewValue = true): void {
    if (this._originalValue != value) {
      if (this.isMultipleField && pushNewValue) {
        let newIds = this._originalValue?.map((val: string) => +val?.split('|')[0]) || [];

        if (
          this.customObjType === RelatedToType.FileResource ||
          this.customObjType === RelatedToType.Company ||
          this.customObjType === RelatedToType.Contact
        ) {
          newIds = this._originalValue;
        }

        if (newIds) {
          value = (value instanceof Array ? [...newIds, ...value] : [...newIds, value]).filter(
            (x) => !(typeof x === 'number'),
          );
        } else {
          value = value instanceof Array ? value : [value];
        }

        value = [...new Set(value)];
      }
      this.update.emit({ [this.fieldDef.name]: value });
      this.updateModel(value);
      this._changeDetectorRef.markForCheck();
    }
  }

  private updateModel(value: any): void {
    this.model[this.fieldDef.name] = value;
    this._originalValue = this.value;
  }

  filterQueryParams(): string | undefined {
    const value = this.getValues();

    if (!value) return undefined;

    let fjson = [[this.fieldDef.name, 'in', [[parseInt(value[0]), value[1]]]]];
    let queryFilter = {
      fjson: JSON.stringify(fjson),
      q: '',
    };

    return JSON.stringify(queryFilter);
  }

  edit(): void {
    if (!this.editing && !this.isDisabled) {
      this.editing = true;
    }
  }

  onBlur(): void {
    this.editing = false;
  }

  compareWithNull(a: any, b: any): boolean {
    return a === b;
  }

  hasNoRecordType(): boolean {
    return !this.fieldDef?.meta?.record_type;
  }

  private setFolderSettings(): void {
    switch (this.data?.relatedToType) {
      case RelatedToType.Project:
        this.foldersTabService.moduleId = (this.data.project as Project).id;
        this.foldersTabService.moduleBaseUrl = 'projects';
        break;
      case RelatedToType.Property:
        this.foldersTabService.moduleId = (this.data.property as Property).id.toString();
        this.foldersTabService.moduleBaseUrl = 'properties';
        break;
      case RelatedToType.Contact:
        this.foldersTabService.moduleId = (this.data.contact as Contact).id;
        this.foldersTabService.moduleBaseUrl = 'contacts';
        break;
      case RelatedToType.Company:
        this.foldersTabService.moduleId = (this.data.company as Company).id;
        this.foldersTabService.moduleBaseUrl = 'companies';
        break;
      case RelatedToType.Task:
        this.foldersTabService.moduleId = (this.model as Task).id.toString();
        this.foldersTabService.moduleBaseUrl = 'tasks';
        break;
      case RelatedToType.CustomObjectValue:
        this.foldersTabService.moduleId = (this.model as CustomObjectValue).id.toString();
        this.foldersTabService.moduleBaseUrl = RelatedToType.CustomObjectValue;
        break;
    }
  }

  private handleTaskFile(file: File): {
    createFileObservable$: Observable<FileResource>;
    uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;
  } {
    const fileResource: FileResource = {
      related_to_type: RelatedToType.Task,
      related_to_id: this.model.id,
      name: file.name,
    };

    const createFileObservable$ = this._accountFoldersService.getFolder().pipe(
      switchMap((folder: FolderResource) => {
        fileResource.folder_resource_id = folder.id;
        return this._accountFoldersService.createFile(folder.id, fileResource);
      }),
    );

    const uploadDocumentObservable = (fileResource: FileResource) =>
      this._accountFoldersService.uploadDocument(fileResource.folder_resource_id, fileResource.id, file);

    return { createFileObservable$, uploadDocumentObservable };
  }

  private handleCustomObjectFile(file: File): {
    createFileObservable$: Observable<FileResource>;
    uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;
  } {
    const folderId = this.model.id;
    const fileResource: FileResource = {
      folder_resource_id: folderId,
      name: file.name,
    };

    const createFileObservable$ = this.foldersTabService.getFolder(null, this.customObject.id).pipe(
      switchMap((folder: FolderResource) => {
        fileResource.folder_resource_id = folder.id;
        return this.foldersTabService.createFile(folder.id, fileResource, this.customObject.id);
      }),
    );

    const uploadDocumentObservable = (fileResource: FileResource) =>
      this.foldersTabService.uploadDocument(
        fileResource.folder_resource_id,
        fileResource.id,
        file,
        this.customObject.id,
      );

    return { createFileObservable$, uploadDocumentObservable };
  }

  private handleDefaultFile(file: File): {
    createFileObservable$: Observable<FileResource>;
    uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;
  } {
    const folderId = this.model?.root_folder_resource_id;
    const fileResource: FileResource = {
      folder_resource_id: folderId,
      name: file.name,
    };

    const createFileObservable$ = this.foldersTabService.createFile(folderId, fileResource);

    const uploadDocumentObservable = (fileResource: FileResource) =>
      this.foldersTabService.uploadDocument(folderId, fileResource.id, file);

    return { createFileObservable$, uploadDocumentObservable };
  }

  onFileChanged(event: InputEvent): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.uploadingFile = true;

    let createFileObservable$: Observable<FileResource>;
    let uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;

    if (this.isRelatedToTask) {
      ({ createFileObservable$, uploadDocumentObservable } = this.handleTaskFile(file));
    } else if (this.isRelatedToCO) {
      ({ createFileObservable$, uploadDocumentObservable } = this.handleCustomObjectFile(file));
    } else {
      ({ createFileObservable$, uploadDocumentObservable } = this.handleDefaultFile(file));
    }

    createFileObservable$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(uploadDocumentObservable),
        catchError((err) => {
          this.snackBar.open('Unable to upload attachment at this moment. Please try again later.', 'error');
          return throwError(() => err);
        }),
        finalize(() => {
          this.uploadingFile = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe((fileResource) => {
        this.fileResource = fileResource;

        if (this.isMultipleField) {
          const value = this.value
            ? [...this.value, `${fileResource.id}|${fileResource.name}`]
            : [`${fileResource.id}|${fileResource.name}`];
          this.value = [...new Set(value)];
        } else {
          this.value = `${fileResource.id}|${fileResource.name}`;
        }

        this.onSave(this.value);
        this.snackBar.open('Successfully uploaded attachment', 'success');
      });
  }

  viewFile(fileResource: FileResource | FileResource[]): void {
    if (!this.value && this.isDisabled) {
      return;
    }

    if (!fileResource) {
      this.errorSnackBar();
      return;
    }

    fileResource = Array.isArray(fileResource) ? fileResource[0] : fileResource;

    this._matDialog.open(FoldersTabViewFileComponent, {
      panelClass: 'mailbox-compose-dialog',
      minWidth: '100vw',
      height: '100vh',
      data: { fileResource },
    });
  }

  downloadFile(file: FileResource): void {
    if (!file) {
      this.errorSnackBar();
      return;
    }

    window.open(file.document_url, '_blank');
  }

  private errorSnackBar(message: string = 'File not found'): void {
    this.snackBar.open(message, 'error', 'Close');
  }

  private getProjectData(): void {
    const vals = this.getValues();

    if (!vals) {
      return;
    }

    if (vals.filter((x) => !x).length > 0) {
      return;
    }

    this.projectsService
      .getProjectById(vals[0])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((project: Project) => {
        this._project = project;
      });
  }

  private handleCustomObjectId(customObjects: CustomObject[]): void {
    if (customObjects?.length) {
      this.customObjectId = customObjects.find(
        (customObject) => customObject.name?.toLowerCase() === this.customObjType?.toLowerCase(),
      )?.id;
    }
  }

  private setTsSelectValue(): void {
    const value = this.model?.[this.fieldDef.name] ?? this.value;
    const supportedTypes = [
      RelatedToType.Company,
      RelatedToType.Contact,
      RelatedToType.Property,
      RelatedToType.Project,
      RelatedToType.User,
    ];

    if (!supportedTypes.includes(this.customObjType as RelatedToType) && !this.isCustomObjectLookup) {
      return;
    }

    if (this.customObjType === RelatedToType.User) {
      const users = this.store.selectSnapshot(UserState.getUsers);
      this.searchOptions = this.addNoneOption(
        users.map((user) => ({
          value: String(user.id),
          label: user.name || user.full_name,
          id: String(user.id),
          name: user.name || user.full_name,
          active: user.active,
        })),
      );

      if (!value && !this.multipleUserIds) {
        this.tsSelectValue = this.isMultipleField ? [] : null;
      } else if (this.isMultipleField) {
        this.tsSelectValue = (this.multipleUserIds || this.value || []).map((id) => String(id));
      } else {
        let userId = value;
        if (typeof value === 'string' && value.includes('|')) {
          userId = value.split('|')[0];
        }
        this.tsSelectValue = userId ? String(userId) : null;
      }

      this._changeDetectorRef.markForCheck();
      setTimeout(() => {
        this._changeDetectorRef.detectChanges();
      }, 100);
      return;
    }

    if (!this.value) {
      this.tsSelectValue = this.isMultipleField ? [] : null;
      return;
    }

    if (this.isMultipleField && Array.isArray(value)) {
      const parsedValues = value
        .map((val) => {
          if (typeof val === 'string' && val.includes('|')) {
            const [id, name] = val.split('|');
            return {
              value: String(id),
              label: name,
              id: String(id),
              name,
            };
          }
          return null;
        })
        .filter(Boolean);

      this.tsSelectValue = parsedValues.map((v) => v.value);
      this.searchOptions = this.addNoneOption(parsedValues);
    } else if (typeof value === 'string' && value.includes('|')) {
      const [id, name] = value.split('|');
      this.tsSelectValue = String(id);
      this.searchOptions = this.addNoneOption([
        {
          value: String(id),
          label: name,
          id: String(id),
          name,
        },
      ]);
    }

    this._changeDetectorRef.markForCheck();
    setTimeout(() => {
      this._changeDetectorRef.detectChanges();
    }, 100);
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    if (this.customObjType === RelatedToType.User) {
      if (!searchTerm || searchTerm.trim().length < 1) {
        const users = this.store.selectSnapshot(UserState.getUsers);
        this.searchOptions = this.addNoneOption(
          users.map((user) => ({
            value: String(user.id),
            label: user.name || user.full_name,
            id: String(user.id),
            name: user.name || user.full_name,
            active: user.active,
          })),
        );
      } else {
        const users = this.store.selectSnapshot(UserState.getUsers);
        const lowerSearch = searchTerm.toLowerCase();
        this.searchOptions = this.addNoneOption(
          this.addCreateNewRecordOption(
            users
              .filter((user) => {
                const name = (user.name || user.full_name || '').toLowerCase();
                return name.includes(lowerSearch);
              })
              .map((user) => ({
                value: String(user.id),
                label: user.name || user.full_name,
                id: String(user.id),
                name: user.name || user.full_name,
                active: user.active,
              })),
          ),
        );
      }
      this._changeDetectorRef.markForCheck();
      return;
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      this.restoreSelectedOptions();
      this._changeDetectorRef.markForCheck();
      return;
    }

    this.isLoadingOptions = true;
    this._changeDetectorRef.markForCheck();

    this.getSearchObservable(searchTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        const newOptions = this.processSearchResponse(response);
        this.searchOptions = this.mergeOptionsWithSelected(newOptions);
        this.isLoadingOptions = false;
        this._changeDetectorRef.markForCheck();
      });
  }

  private restoreSelectedOptions(): void {
    if (!this.tsSelectValue) {
      this.searchOptions = this.addNoneOption([]);
      return;
    }

    if (this.isMultipleField && Array.isArray(this.tsSelectValue)) {
      const selectedIds = this.tsSelectValue.map((id) => String(id));
      this.searchOptions = this.addNoneOption(
        this.searchOptions.filter(
          (opt) => selectedIds.includes(String(opt.value)) && !opt.isCreateNewOption && !opt.isNoneOption,
        ),
      );
    } else {
      this.searchOptions = this.addNoneOption(
        this.searchOptions.filter(
          (opt) => String(opt.value) === String(this.tsSelectValue) && !opt.isCreateNewOption && !opt.isNoneOption,
        ),
      );
    }
  }

  private mergeOptionsWithSelected(newOptions: any[]): any[] {
    if (!this.tsSelectValue) {
      return this.addNoneOption(this.addCreateNewRecordOption(newOptions));
    }

    const selectedIds = this.isMultipleField
      ? Array.isArray(this.tsSelectValue)
        ? this.tsSelectValue.map((id) => String(id))
        : []
      : [String(this.tsSelectValue)];

    const selectedOptions = this.searchOptions.filter(
      (opt) => selectedIds.includes(String(opt.value)) && !opt.isCreateNewOption,
    );

    const optionsMap = new Map();

    selectedOptions.forEach((opt) => optionsMap.set(opt.value, opt));

    newOptions.forEach((opt) => optionsMap.set(opt.value, opt));

    return this.addNoneOption(this.addCreateNewRecordOption(Array.from(optionsMap.values())));
  }

  private addCreateNewRecordOption(options: any[]): any[] {
    if (!this.canCreateNewRecord()) {
      return options;
    }

    return [
      ...options,
      {
        value: 'CREATE_NEW',
        label: '+ Create a New Record',
        id: 'CREATE_NEW',
        name: '+ Create a New Record',
        isCreateNewOption: true,
      },
    ];
  }

  private addNoneOption(options: any[]): any[] {
    if (this.isMultipleField) {
      return options;
    }

    if (this.value) {
      return [
        {
          value: null,
          label: 'None',
          id: 'NONE',
          name: 'None',
          isNoneOption: true,
        },
        ...options,
      ];
    }
    return options;
  }

  private getSearchObservable(value: string): Observable<any> {
    if (this.isCustomObject(this.customObjType)) {
      return this.lookupService.lookFor({ query: value, relatedTo: this.customObjType });
    }

    const searchHandlers = {
      [RelatedToType.Property]: () =>
        this.propertiesService.getProperties(
          ['id', 'name'],
          {},
          0,
          20,
          'name',
          'asc',
          value,
          '',
          '',
          this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS)),
          ['name'],
        ),
      [RelatedToType.Project]: () => {
        const fieldsToSearch = ['name', 'title'];
        return this.projectsService.getProjectsNoPipeline(
          ['id', 'name'],
          {},
          0,
          20,
          'title',
          'asc',
          value,
          false,
          null,
          false,
          null,
          this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS)),
          fieldsToSearch,
        );
      },
      [RelatedToType.Company]: () => {
        return this.lookupService.lookFor({ query: value, relatedTo: RelatedToType.Company });
      },
      [RelatedToType.Contact]: () => {
        return this.lookupService.lookFor({ query: value, relatedTo: RelatedToType.Contact });
      },
    };

    const handler = searchHandlers[this.customObjType];

    if (!handler) {
      return of([]);
    }

    return handler ? handler() : of([]);
  }

  private processSearchResponse(response: any): any[] {
    if (this.isCustomObject(this.customObjType)) {
      return response.map((coValue) => ({
        value: String(coValue.id),
        label: coValue.label,
        id: String(coValue.id),
        name: coValue.label,
      }));
    }

    const handlers = {
      [RelatedToType.Property]: (res) => {
        return (
          res?.properties?.map((item) => ({
            value: String(item.id),
            label: item.name || item.title || `Property ${item.id}`,
            id: String(item.id),
            name: item.name || item.title || `Property ${item.id}`,
          })) || []
        );
      },
      [RelatedToType.Project]: (res) => {
        return (
          res?.projects?.map((item) => ({
            value: String(item.id),
            label: item.name || item.title || `Project ${item.id}`,
            id: String(item.id),
            name: item.name || item.title || `Project ${item.id}`,
          })) || []
        );
      },
      [RelatedToType.Company]: (res) => {
        return res.map((item) => ({
          value: String(item.id),
          label: item.label,
          id: String(item.id),
          name: item.label,
        }));
      },
      [RelatedToType.Contact]: (res) => {
        return res.map((item) => ({
          value: String(item.id),
          label: item.label,
          id: String(item.id),
          name: item.label,
        }));
      },
    };

    const handler = handlers[this.customObjType];

    if (!handler) {
      return [];
    }

    return handler ? handler(response) : [];
  }

  private isCustomObject(name: string): boolean {
    if (STANDARD_RELATED_TO_TYPES.includes(name as any)) {
      return false;
    }

    const isCustom = this.customObjects?.some((co) => co.name === name);
    return isCustom;
  }

  onTsSelectChange(selectedValue: any): void {
    if (selectedValue === 'CREATE_NEW' || (Array.isArray(selectedValue) && selectedValue.includes('CREATE_NEW'))) {
      this.openTypeRelatedModal();
      this._changeDetectorRef.detectChanges();
      return;
    }

    this.tsSelectValue = selectedValue;
    if (selectedValue === null || selectedValue === 'null' || (!selectedValue && !this.isMultipleField)) {
      this.value = null;
      this.tsSelectValue = null;
      if (this.customObjType === RelatedToType.User && this.isMultipleField) {
        this.multipleUserIds = [];
      }
      this.update.emit({ [this.fieldDef.name]: null });
      this.updateModel(null);
      this._changeDetectorRef.markForCheck();
      return;
    }

    if (!selectedValue || (Array.isArray(selectedValue) && selectedValue.length === 0)) {
      this.value = this.isMultipleField ? [] : null;
      if (this.customObjType === RelatedToType.User && this.isMultipleField) {
        this.multipleUserIds = [];
      }
      this.update.emit({ [this.fieldDef.name]: this.value });
      this.updateModel(this.value);
      this._changeDetectorRef.markForCheck();
      return;
    }

    if (this.customObjType === RelatedToType.User) {
      if (this.isMultipleField) {
        const numericIds = selectedValue.map((id) => Number(id));
        this.multipleUserIds = numericIds;
        this.value = numericIds;
      } else {
        this.value = Number(selectedValue);
      }
      this.update.emit({ [this.fieldDef.name]: this.value });
      this.updateModel(this.value);
      this._changeDetectorRef.markForCheck();
      return;
    }

    if (!this.isMultipleField) {
      const selectedOption = this.searchOptions.find((opt) => String(opt.value) === String(selectedValue));
      if (selectedOption) {
        const formattedValue = `${selectedOption.value}|${selectedOption.label}`;
        this.value = formattedValue;
        this.update.emit({ [this.fieldDef.name]: this.value });
        this.updateModel(this.value);
      }
    } else {
      const formattedValues = selectedValue
        .filter((id) => id !== 'CREATE_NEW')
        .map((id) => {
          const option = this.searchOptions.find((opt) => String(opt.value) === String(id));
          return option ? `${option.value}|${option.label}` : null;
        })
        .filter(Boolean);

      this.value = formattedValues;
      this.update.emit({ [this.fieldDef.name]: this.value });
      this.updateModel(this.value);
    }

    this._changeDetectorRef.markForCheck();
  }

  canCreateNewRecord(): boolean {
    if (this.customObjType === RelatedToType.Contact) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.CONTACTS_CREATE)) ||
        this.store.selectSnapshot(UserState.hasPermission(Permission.CONTACTS_ADD))
      );
    }

    if (this.customObjType === RelatedToType.Company) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.COMPANIES_CREATE)) ||
        this.store.selectSnapshot(UserState.hasPermission(Permission.CONTACTS_ADD))
      );
    }

    if (this.customObjType === RelatedToType.Project) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_CREATE)) ||
        this.store.selectSnapshot(UserState.hasPermission(Permission.DEALS_ADD))
      );
    }

    if (this.customObjType === RelatedToType.Property) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_CREATE)) ||
        this.store.selectSnapshot(UserState.hasPermission(Permission.PROPERTY_CREATE))
      );
    }

    if (this.isCustomObject(this.customObjType)) {
      if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
        return this.store.selectSnapshot(
          UserState.hasPermission(PermissionUtils.generateCustomObjectPermissions(this.customObjType).CREATE),
        );
      } else {
        return true;
      }
    }
    return false;
  }
}
